// ToneBadge Component - E007-S018
// Color-coded badge displaying emotional tone with emoji and confidence
import React from 'react';
import { EmotionalTone, TONE_CONFIG } from './EmotionalToneFilter';

interface Props {
  tone: EmotionalTone;
  confidence?: number;
  showConfidence?: boolean;
  size?: 'sm' | 'md' | 'lg';
  onClick?: () => void;
  isManualOverride?: boolean;
}

const sizeClasses = {
  sm: 'text-xs px-2 py-0.5',
  md: 'text-sm px-2.5 py-1',
  lg: 'text-base px-3 py-1.5'
};

export const ToneBadge: React.FC<Props> = ({
  tone,
  confidence,
  showConfidence = false,
  size = 'md',
  onClick,
  isManualOverride = false
}) => {
  const config = TONE_CONFIG.find(t => t.name === tone);
  
  if (!config) return null;
  
  const confidencePercent = confidence ? Math.round(confidence * 100) : null;
  
  return (
    <span
      onClick={onClick}
      className={`
        inline-flex items-center gap-1.5 rounded-full font-medium
        ${sizeClasses[size]}
        ${config.bgColor}
        ${config.color}
        ${onClick ? 'cursor-pointer' : ''}
        transition-all
      `}
      title={`${config.description}${confidencePercent ? ` (${confidencePercent}% confidence)` : ''}`}
    >
      <span>{config.emoji}</span>
      <span className="capitalize">{tone}</span>
      
      {showConfidence && confidencePercent !== null && (
        <span className="text-opacity-70 text-xs">
          {confidencePercent}%
        </span>
      )}
      
      {isManualOverride && (
        <span title="Manually classified" className="text-xs">⚠️</span>
      )}
    </span>
  );
};

// Smaller inline version for lists
export const ToneBadgeInline: React.FC<{ tone: EmotionalTone }> = ({ tone }) => {
  const config = TONE_CONFIG.find(t => t.name === tone);
  if (!config) return null;
  
  return (
    <span className={`inline-flex items-center gap-1 ${config.color}`}>
      <span>{config.emoji}</span>
      <span className="capitalize text-sm">{tone}</span>
    </span>
  );
};

// Get tone color for charts
export const getToneColor = (tone: EmotionalTone): string => {
  const colors: Record<EmotionalTone, string> = {
    urgency: '#ef4444',  // red-500
    hope: '#22c55e',     // green-500
    fear: '#f97316',     // orange-500
    joy: '#eab308',      // yellow-500
    neutral: '#6b7280'   // gray-500
  };
  return colors[tone];
};

export default ToneBadge;
